import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { FirstVisitBloc } from '@kbloc';
import { NotificationsApi } from '@kp/notifications/notifications.api';
import { Constants } from '@kp/shared/constants.service';
import { WINDOW } from '@kservice';
import { CardEventType, FirstVisitCase } from '@ktypes/enums';
import { DataStatus, JsonObject, NotificationDevice, NotificationInfo, Status } from '@ktypes/models';
import { DateTimeUtil, DaysWeekAbbreviation, Popup } from '@kutil';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export enum NotificationPermissionStatus {
  enabled = 'enabled',
  notNow = 'notNow',
}

export enum NotificationPermissionState {
  default = 'default',
  granted = 'granted',
  denied = 'denied',
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsBloc {
  constructor(
    public afMessaging: AngularFireMessaging,
    @Inject(DOCUMENT) private _document: Document,
    private _firstVisitBloc: FirstVisitBloc,
    private _notificationsApi: NotificationsApi,
    private _popupService: Popup,
    @Inject(WINDOW) private _window: Window
  ) {}

  private _notificationSubject = new BehaviorSubject<DataStatus<NotificationInfo[]>>(null);

  private _updatedNotification$ = new Subject<NotificationInfo>();

  private _devicesSubject = new BehaviorSubject<DataStatus<NotificationDevice[]>>(null);

  get notificationSubject$(): Observable<DataStatus<NotificationInfo[]>> {
    return this._notificationSubject.asObservable();
  }

  get updatedNotification$(): Observable<NotificationInfo> {
    return this._updatedNotification$.asObservable();
  }

  get devicesSubject$(): Observable<DataStatus<NotificationDevice[]>> {
    return this._devicesSubject.asObservable();
  }

  isSupported(): boolean {
    return 'PushManager' in this._window && 'Notification' in this._window;
  }

  refresh(): void {
    this._notificationSubject.next(new DataStatus<NotificationInfo[]>(Status.starting, null, null));
    this._notificationsApi.getNotifications().then((notificationInfo) => {
      this._notificationSubject.next(
        new DataStatus<NotificationInfo[]>(notificationInfo ? Status.done : Status.error, null, notificationInfo)
      );
    });
  }

  updateNotification(notificationToUpdate: NotificationInfo): void {
    this._notificationsApi.updateNotification(notificationToUpdate).then((notification) => {
      this._updatedNotification$.next(notification);
      this.refresh();
    });
  }

  createNotification(notificationToCreate: NotificationInfo): void {
    this._notificationsApi.createNotification(notificationToCreate).then((notification) => {
      this._updatedNotification$.next(notification);
      this.refresh();
    });
  }

  deleteNotification(notificationToDelete: NotificationInfo): void {
    this._notificationsApi.deleteNotification(notificationToDelete).then(() => {
      this.refresh();
    });
  }

  handleCardNotificationUpdates(cardEventType: CardEventType, notification: NotificationInfo): void {
    switch (cardEventType) {
      case CardEventType.REMINDER_EDITED:
        if (notification?.id) {
          this.updateNotification(notification);
        } else {
          this.createNotification(notification);
        }
        break;
      case CardEventType.REMINDER_DELETED:
        if (!notification?.id) {
          return;
        }
        this.deleteNotification(notification);
        break;
    }
  }

  registerDevice(registrationToken: string): void {
    const json: JsonObject = {};
    json['notificationPlatform'] = Constants.platformName;
    json['registrationToken'] = registrationToken;
    this._notificationsApi.createDevice(json);
  }

  getDevices(): void {
    this._devicesSubject.next(new DataStatus<NotificationDevice[]>(Status.starting, null, null));
    this._notificationsApi.getDevices().then((devices) => {
      this._devicesSubject.next(new DataStatus<NotificationDevice[]>(Status.done, null, devices));
    });
  }

  async requestPermission(): Promise<void> {
    this.afMessaging.requestToken.subscribe(
      (token) => {
        this.registerDevice(token);
      },
      (error) => {
        console.warn('Unable to request Firebase token: ', error);
      }
    );
  }

  deleteWebDevices(deviceList: NotificationDevice[]): void {
    for (const device of deviceList) {
      if (device.platformName === Constants.platformName) {
        this._notificationsApi.deleteDevice(device.id);
      }
    }
    const updatedList = deviceList.filter((device) => device.platformName !== Constants.platformName);
    this._devicesSubject.next(new DataStatus<NotificationDevice[]>(Status.done, null, updatedList));
  }

  clearIoNotifications() {
    const popup = this._popupService.initialize({
      autoOpen: true,
      url: `${this._document.location.origin.replace('.kumanu.com', '.io')}/unregister-sw.html`,
      name: 'notifications',
      windowFeatures: {
        width: 50,
        height: 50,
      },
    });
    // ensure popup closes after 4s
    setTimeout(() => !popup?.closed && popup?.close(), 4000);
    this._firstVisitBloc.updateFirstVisit(FirstVisitCase.clearedIoNotifications);
  }

  getSelectedDays(days: DaysWeekAbbreviation[]): string {
    if (DateTimeUtil.everyDay.every((day) => days.includes(day))) {
      return 'Every Day';
    } else if (days?.length === 5 && DateTimeUtil.weekdays.every((day) => days.includes(day))) {
      return 'Weekdays';
    } else if (days?.length === 2 && DateTimeUtil.weekends.every((day) => days.includes(day))) {
      return 'Weekends';
    } else {
      if (days.length === 1) {
        return days[0];
      } else {
        const capitalized = DateTimeUtil.everyDay.filter((day) => days.includes(day)).map((day) => day);
        return capitalized.join(', ');
      }
    }
  }
}
